'use client';

import * as React from 'react';
import { useEventCallback } from '@base-ui-components/utils/useEventCallback';
import { useClick, useDismiss, useFloatingRootContext, useInteractions, useRole } from "../../floating-ui-react/index.js";
import { getTarget } from "../../floating-ui-react/utils.js";
import { useScrollLock } from "../../utils/useScrollLock.js";
import { useTransitionStatus } from "../../utils/useTransitionStatus.js";
import { useOpenInteractionType } from "../../utils/useOpenInteractionType.js";
import { useOpenChangeComplete } from "../../utils/useOpenChangeComplete.js";
export function useDialogRoot(params) {
  const {
    store,
    parentContext
  } = params;
  const open = store.useState('open');
  const dismissible = store.useState('dismissible');
  const modal = store.useState('modal');
  const triggerElement = store.useState('triggerElement');
  const popupElement = store.useState('popupElement');
  const {
    mounted,
    setMounted,
    transitionStatus
  } = useTransitionStatus(open);
  const {
    openMethod,
    triggerProps,
    reset: resetOpenInteractionType
  } = useOpenInteractionType(open);
  const handleUnmount = useEventCallback(() => {
    setMounted(false);
    store.context.openChangeComplete?.(false);
    resetOpenInteractionType();
  });
  useOpenChangeComplete({
    enabled: !params.actionsRef,
    open,
    ref: store.context.popupRef,
    onComplete() {
      if (!open) {
        handleUnmount();
      }
    }
  });
  React.useImperativeHandle(params.actionsRef, () => ({
    unmount: handleUnmount
  }), [handleUnmount]);
  const context = useFloatingRootContext({
    elements: {
      reference: triggerElement,
      floating: popupElement
    },
    open,
    onOpenChange: store.setOpen,
    noEmit: true
  });
  const [ownNestedOpenDialogs, setOwnNestedOpenDialogs] = React.useState(0);
  const isTopmost = ownNestedOpenDialogs === 0;
  const role = useRole(context);
  const click = useClick(context);
  const dismiss = useDismiss(context, {
    outsidePressEvent() {
      if (store.context.internalBackdropRef.current || store.context.backdropRef.current) {
        return 'intentional';
      }
      // Ensure `aria-hidden` on outside elements is removed immediately
      // on outside press when trapping focus.
      return {
        mouse: modal === 'trap-focus' ? 'sloppy' : 'intentional',
        touch: 'sloppy'
      };
    },
    outsidePress(event) {
      if (event.button !== 0) {
        return false;
      }
      const target = getTarget(event);
      if (isTopmost && dismissible) {
        const eventTarget = target;
        // Only close if the click occurred on the dialog's owning backdrop.
        // This supports multiple modal dialogs that aren't nested in the React tree:
        // https://github.com/mui/base-ui/issues/1320
        if (modal) {
          return store.context.internalBackdropRef.current || store.context.backdropRef.current ? store.context.internalBackdropRef.current === eventTarget || store.context.backdropRef.current === eventTarget : true;
        }
        return true;
      }
      return false;
    },
    escapeKey: isTopmost
  });
  useScrollLock({
    enabled: open && modal === true,
    mounted,
    open,
    referenceElement: popupElement
  });
  const {
    getReferenceProps,
    getFloatingProps
  } = useInteractions([role, click, dismiss]);

  // Listen for nested open/close events on this store to maintain the count
  store.useContextCallback('nestedDialogOpen', ownChildrenCount => {
    setOwnNestedOpenDialogs(ownChildrenCount + 1);
  });
  store.useContextCallback('nestedDialogClose', () => {
    setOwnNestedOpenDialogs(0);
  });

  // Notify parent of our open/close state using parent callbacks, if any
  React.useEffect(() => {
    if (parentContext?.nestedDialogOpen && open) {
      parentContext.nestedDialogOpen(ownNestedOpenDialogs);
    }
    if (parentContext?.nestedDialogClose && !open) {
      parentContext.nestedDialogClose();
    }
    return () => {
      if (parentContext?.nestedDialogClose && open) {
        parentContext.nestedDialogClose();
      }
    };
  }, [open, parentContext, ownNestedOpenDialogs]);
  const dialogTriggerProps = React.useMemo(() => getReferenceProps(triggerProps), [getReferenceProps, triggerProps]);
  store.useSyncedValues({
    openMethod,
    mounted,
    transitionStatus,
    triggerProps: dialogTriggerProps,
    popupProps: getFloatingProps(),
    floatingRootContext: context,
    nestedOpenDialogCount: ownNestedOpenDialogs
  });
}